import React from 'react';
import * as Yup from 'yup';
import { Form } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { UserPanelRegistrationInformation } from '../types';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { formatRegistration } from '../util';
import { Done } from './Done';
import { useCurrentUser } from '@economic/react-app';

const phoneRegEx = new RegExp(
    '^\\s*(?:\\+?(\\d{1,3}))?([-. (]*(\\d{3})[-. )]*)?((\\d{3})[-. ]*(\\d{2,4})(?:[-.x ]*(\\d+))?)\\s*$'
);

interface Props {
    data?: UserPanelRegistrationInformation;
    onCancel: () => void;
    refresh: () => void;
    step?: number;
}

export const Wizard = ({ data, onCancel, refresh, step = undefined }: Props) => {
    const user = useCurrentUser();
    const [currentStep, setCurrentStep] = React.useState(step || 1);

    const initialData = {
        name: `${user.firstName} ${user.lastName ?? ''}`,
        email: user.email,
        ...data,
    };

    const singleStepOnly = !!step;

    const notifications = singleStepOnly
        ? {
              success: t`USER_PANEL_SAVE_SUCCESS`,
          }
        : undefined;

    const handleSubmit = async values => {
        try {
            let response;

            if (data?.userGuid) {
                response = await internalApiClient.put(`/user-panel`, formatRegistration(values));
                refresh();
            } else {
                response = await internalApiClient.post(`/user-panel`, formatRegistration(values));
            }

            return Promise.resolve(response);
        } catch (error) {
            if (error.response && error.response.data) {
                error.validation = error.response.data;
            }

            return Promise.reject(error);
        }
    };
    const handleSubmitted = singleStepOnly ? onCancel : () => setCurrentStep(4);
    const handleBack = singleStepOnly ? undefined : () => setCurrentStep(currentStep => currentStep - 1);
    const handleNext = singleStepOnly ? undefined : () => setCurrentStep(currentStep => currentStep + 1);
    const handleDone = () => {
        refresh();
        onCancel();
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .max(
                250,
                t(`FORM_FIELD_ERROR_MAX_N_CHARACTERS`, {
                    FIELD: t`FORM_FIELD_LABEL_NAME`,
                    NUMBER: 250,
                })
            )
            .required(),
        email: Yup.string()
            .email(t`USER_PANEL_VALID_EMAIL_MESSAGE`)
            .max(
                250,
                t(`FORM_FIELD_ERROR_MAX_N_CHARACTERS`, {
                    FIELD: t`FORM_FIELD_LABEL_EMAIL`,
                    NUMBER: 250,
                })
            )
            .required(),
        phone: Yup.string()
            .matches(RegExp(phoneRegEx), t`USER_PANEL_VALID_PHONE_MESSAGE`)
            .max(
                50,
                t(`FORM_FIELD_ERROR_MAX_N_CHARACTERS`, {
                    FIELD: t`FORM_FIELD_LABEL_PHONE`,
                    NUMBER: 50,
                })
            )
            .required(),
        city: Yup.string()
            .max(
                250,
                t(`FORM_FIELD_ERROR_MAX_N_CHARACTERS`, {
                    FIELD: t`FORM_FIELD_LABEL_ADDRESS_TOWN`,
                    NUMBER: 250,
                })
            )
            .required(),
    });

    return (
        <div className="user_panel_wizard relative my-6 mx-auto h-128 w-9/12">
            <Form
                initialData={initialData}
                messages={notifications}
                onSubmit={handleSubmit}
                onSubmitted={handleSubmitted}
                skipNotifications={!singleStepOnly}
                validationSchema={validationSchema}>
                {({ handleSubmit, values }) => (
                    <>
                        {currentStep === 1 && <Step1 onNext={handleNext} values={values} />}
                        {currentStep === 2 && <Step2 onBack={handleBack} onNext={handleNext} values={values} />}
                        {currentStep === 3 && (
                            <Step3
                                onBack={handleBack}
                                onNext={!singleStepOnly ? handleSubmit : undefined}
                                onSubmit={singleStepOnly ? handleSubmit : undefined}
                                values={values}
                            />
                        )}
                        {currentStep === 4 && <Done onDone={handleDone} />}
                    </>
                )}
            </Form>
        </div>
    );
};
